import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { FieldConfig, TraceRow } from './trace-table-config';

interface TraceTableCellProps {
	fieldConfig:
		| FieldConfig
		| { key: null; label: string; sortable: false; className?: string };
	trace: TraceRow;
	cellIndex: number;
	hasChildren: boolean;
	isExpanded: boolean;
	onToggleExpansion: (traceId: string) => void;
	onSessionClick: (sessionId: string) => void;
}

export const TraceTableCell = ({
	fieldConfig,
	trace,
	cellIndex,
	hasChildren,
	isExpanded,
	onToggleExpansion,
	onSessionClick,
}: TraceTableCellProps): JSX.Element | null => {
	if (cellIndex === 0) {
		// Expansion toggle column
		return (
			<>
				{!trace.isChild && hasChildren && (
					<Button
						variant='ghost'
						size='sm'
						className='tw-h-6 tw-w-6 tw-p-0'
						onClick={(e) => {
							e.stopPropagation();
							onToggleExpansion(trace.id);
						}}
					>
						{isExpanded ? (
							<ChevronDown className='tw-h-4 tw-w-4' />
						) : (
							<ChevronRight className='tw-h-4 tw-w-4' />
						)}
					</Button>
				)}
				{trace.isChild && (
					<div className='tw-ml-4 tw-text-blue-500 tw-text-xs'>↳</div>
				)}
			</>
		);
	}

	if (fieldConfig.key === 'session_id') {
		// Special handling for session ID as a clickable button
		return (
			<Button
				variant='ghost'
				size='sm'
				className='tw-text-xs tw-p-1 tw-h-auto tw-font-mono tw-text-blue-600 hover:tw-text-blue-800 hover:tw-bg-blue-50 tw-border tw-border-transparent hover:tw-border-blue-200 tw-transition-all tw-duration-200'
				onClick={(e) => {
					e.stopPropagation();
					onSessionClick(trace.session_id);
				}}
				title='Click to filter by this session ID'
			>
				{trace.session_id}
			</Button>
		);
	}

	if ('formatter' in fieldConfig && fieldConfig.formatter) {
		// Dynamic field rendering with formatter
		const formatted = fieldConfig.formatter(
			fieldConfig.key ? trace[fieldConfig.key] : null,
			trace,
		);
		return <>{formatted}</>;
	}

	if (fieldConfig.key) {
		// Raw field value
		const value = trace[fieldConfig.key];
		return <>{value}</>;
	}

	return null;
};
